import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { EVENT_TYPES } from '@/services/api/schemas/events'
import {
  EVENTS_PERIODS,
  EVENTS_PERIOD_LABELS,
  getEventsPeriodFrom,
  type EventsPeriod,
} from '@/services/events/eventsPeriod'
import { useEventsFilters } from '@/services/events/useEventsFilters'
import { useMediaQuery } from '@/shared/hooks/useMediaQuery'
import { useOnIntersect } from '@/shared/hooks/useOnIntersect'
import { EVENTS_STACKED_QUERY } from '@/shared/lib/breakpoints'
import { getErrorMessage, plural } from '@/shared/lib/format'
import grid from '@/shared/styles/layout.module.css'
import { EventRow, Panel, Select, Skeleton, StateBlock } from '@/shared/ui'

import { EventDetail } from './components/EventDetail'
import { EventDetailSheet } from './components/EventDetailSheet'
import { EventFilters } from './components/EventFilters'
import s from './EventsPage.module.css'
import {
  useEventTypeCounts,
  useEventsQuery,
  type CountKey,
  type EventsListParams,
} from './hooks/useEventsQuery'

const COUNT_KEYS: readonly CountKey[] = ['all', ...EVENT_TYPES]

const PERIOD_OPTIONS = EVENTS_PERIODS.map((value) => ({
  value,
  label: EVENTS_PERIOD_LABELS[value],
}))

export function EventsPage() {
  const filters = useEventsFilters()

  // Границу периода считаем один раз на смену фильтра, иначе ключ запроса менялся бы каждый рендер.
  const from = useMemo(() => getEventsPeriodFrom(filters.period), [filters.period])
  const params = useMemo<EventsListParams>(
    () => ({ type: filters.type === 'all' ? undefined : filters.type, from }),
    [filters.type, from],
  )
  const query = useEventsQuery(params)
  const items = useMemo(() => query.data?.items ?? [], [query.data])
  const total = query.data?.total

  // Бесконечная прокрутка: следующая страница подгружается, когда строка-заглушка
  // в конце списка попадает в область видимости.
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query
  const sentinelRef = useRef<HTMLDivElement>(null)
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage])
  useOnIntersect(
    sentinelRef,
    loadMore,
    hasNextPage && !isFetchingNextPage && !query.isPlaceholderData,
  )

  // Счётчики чипов. Если журнал загружен целиком — считаем по списку без запросов.
  // Иначе total выбранного фильтра уже известен из списка, остальные ключи (типы и «Все»)
  // добираем запросами limit=1. Пока после смены фильтра показывается placeholder — список
  // прошлого фильтра, — его total и состав к новому фильтру не относятся и в счётчики не идут.
  const settled = query.data !== undefined && !query.isPlaceholderData
  const listLoaded = settled && total !== undefined
  const canDeriveCounts = filters.type === 'all' && listLoaded && total <= items.length
  const derivedCounts = useMemo<Partial<Record<CountKey, number>> | undefined>(() => {
    if (!canDeriveCounts) return undefined
    const result: Partial<Record<CountKey, number>> = Object.fromEntries(
      EVENT_TYPES.map((t) => [t, 0]),
    )
    for (const event of items)
      if (event.type !== 'unknown') result[event.type] = (result[event.type] ?? 0) + 1
    result.all = items.length
    return result
  }, [canDeriveCounts, items])
  const missingKeys = useMemo(() => COUNT_KEYS.filter((k) => k !== filters.type), [filters.type])
  const fetchedCounts = useEventTypeCounts(from, missingKeys, listLoaded && !canDeriveCounts)
  const counts = useMemo<Partial<Record<CountKey, number>>>(() => {
    if (derivedCounts) return derivedCounts
    return listLoaded ? { ...fetchedCounts, [filters.type]: total } : fetchedCounts
  }, [derivedCounts, fetchedCounts, filters.type, listLoaded, total])
  // Чипы показывают последние известные числа и меняют их только на новые: иначе при смене
  // фильтра цифры на мгновение пропадали бы или подставлялись из списка прошлого фильтра.
  const [shownCounts, setShownCounts] = useState<Partial<Record<CountKey, number>>>({})
  useEffect(() => {
    setShownCounts((prev) => {
      const next = { ...prev, ...counts }
      const same = Object.keys(next).every((key) => next[key as CountKey] === prev[key as CountKey])
      return same && Object.keys(next).length === Object.keys(prev).length ? prev : next
    })
  }, [counts])

  const selected = items.find((event) => event.id === filters.selectedId) ?? items[0]

  // На узких экранах правой колонки нет: детали открываются поверх журнала по тапу.
  const stacked = useMediaQuery(EVENTS_STACKED_QUERY)
  const [sheetOpen, setSheetOpen] = useState(false)
  const closeSheet = useCallback(() => setSheetOpen(false), [])
  useEffect(() => {
    if (!stacked) setSheetOpen(false)
  }, [stacked])
  const openEvent = (id: string) => {
    filters.select(id)
    if (stacked) setSheetOpen(true)
  }

  return (
    <div className={grid.grid}>
      <EventFilters value={filters.type} onChange={filters.setType} counts={shownCounts} />

      <div className={s.layout}>
        <Panel
          title="Журнал"
          subtitle={
            total !== undefined
              ? `${total} ${plural(total, 'событие', 'события', 'событий')}`
              : undefined
          }
          flush
        >
          <div className={s.periodRow}>
            <label className={s.periodLabel} htmlFor="events-period">
              Период
            </label>
            <Select<EventsPeriod>
              id="events-period"
              value={filters.period}
              onChange={filters.setPeriod}
              options={PERIOD_OPTIONS}
              aria-label="Период"
            />
          </div>
          <div className={s.list}>
            {query.error && (
              <StateBlock
                kind="error"
                message={`Не удалось получить события. ${getErrorMessage(query.error)}`}
                onRetry={() => void query.refetch()}
              />
            )}
            {query.isPending &&
              Array.from({ length: 6 }, (_, i) => (
                <div key={i} className={s.skeletonRow}>
                  <Skeleton height={34} />
                </div>
              ))}
            {/* ТЗ п. 7.1–7.2: пустой журнал и пустой результат фильтров — разные тексты. */}
            {query.data && items.length === 0 && (
              <StateBlock
                kind="empty"
                message={
                  filters.type !== 'all' || filters.period !== 'all'
                    ? 'События по выбранным параметрам не найдены'
                    : 'Событий пока нет'
                }
              />
            )}
            {items.map((event) => (
              <EventRow
                key={event.id}
                event={event}
                selected={event.id === selected?.id}
                onClick={(e) => openEvent(e.id)}
              />
            ))}
            {hasNextPage && (
              <div
                ref={sentinelRef}
                className={s.skeletonRow}
                role="status"
                aria-busy={isFetchingNextPage}
                aria-label="Загрузка следующих событий"
              >
                <Skeleton height={34} />
              </div>
            )}
          </div>
        </Panel>

        {!stacked && (
          <Panel flush>
            {query.isPending ? (
              <div style={{ padding: 24 }}>
                <Skeleton height={320} />
              </div>
            ) : (
              <EventDetail event={selected} />
            )}
          </Panel>
        )}
      </div>

      {stacked && sheetOpen && selected && (
        <EventDetailSheet event={selected} onClose={closeSheet} />
      )}
    </div>
  )
}
